import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { clamp, damp } from '@/math/angles';
import {
  PartBuilder,
  SpritePool,
  alignY,
  numberInk,
  numberTexture,
  type InkBox,
  roundelTexture,
  yawTilt,
  type ModMaterials,
} from './modKit';
import type { ModCtx, ModPart, PartFn, PartOpts } from './modTypes';
import { fanHeadCentre, fanLayout, fanVolume, restTopAt, roofHalfAt } from './modProfile';
import type { ModAnchors } from './modAnchors';
import type { SurfaceField } from './modSurface';
import { clearMount } from './modMount';
import {
  TOP_DECAL_STRETCH,
  decalSeenOpacity,
  decalSeenShare,
  flankDecalGeometry,
  flankDecalSpot,
  topDecalGeometry,
  topDecalSpot,
  type FlankBox,
} from './modDecal';
import { capeShape } from './capeShape';
import { tifoSpec } from './partsSoft';

/**
 * partsHard.ts (M128) — the rigid mods: gloves, floodlights, kit number,
 * cleats, whistles, rally pod, trophy, corner flags, goal wing, mud flaps.
 * Every builder reads its ride's measured anchors (modAnchors) and the kit's
 * two colours; motion is a few hinge angles, uniforms and sprite bursts fed
 * by the car's render snapshot. Numbers are the concept renders' numbers
 * (the same proportions the owner approved), in car-local units.
 */

const WHITE = 0xf1f1ee;
const DARK = 0x15161a;
const GOLD = 0xe8b23a;
const BRASS = 0xf1c76a;
const GREEN = 0x1f9d55;
const GALV = 0xc9cdd2;
const CHROME = 0xdfe3e8;
const RUBBER = 0x24252a;
const LATEX = 0xf5f5f2;
const LAMP = 0xfff3cc;
const SOLE = 0xe6e6e2;
const STUD = 0xdcdde0;

const R = (deg: number): number => (deg * Math.PI) / 180;
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const windOf = (ctx: ModCtx): number => clamp01(ctx.speed / 35);
const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

/** M153: the body's measured height at (x, z) once the model is in (the
 *  anchor-table height before, or where nothing was measured). */
function bodyY(opts: PartOpts, x: number, z: number, fallback: number): number {
  return opts.surface?.topAt(x, z) ?? fallback;
}

/** M153: a deck spot clear of the stock rear wing (the floods, flags and the
 *  M128 trunk spots stood ON the coupe's GT wing endplates): moved forward of
 *  the blade's leading edge when it lands under the wing. */
export function clearOfWing(a: ModAnchors, z: number, margin: number): number {
  if (!a.wing) return z;
  const lead = a.wing.z + a.wing.chord / 2 + margin;
  return z < lead ? lead : z;
}

function coneMaterial(m: ModMaterials, color: number): THREE.MeshBasicMaterial {
  return m.track(
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    }),
  );
}

/** A light cone: apex at `apex`, opening along `dir`. */
function lightCone(apex: THREE.Vector3, dir: THREE.Vector3, rBase: number, len: number): THREE.BufferGeometry {
  const geo = new THREE.ConeGeometry(rBase, len, 12, 1, true);
  const d = dir.clone().normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(V(0, -1, 0), d);
  geo.applyMatrix4(new THREE.Matrix4().compose(apex.clone().addScaledVector(d, len / 2), q, V(1, 1, 1)));
  return geo;
}

/** The trophy topper's size (the bike's is smaller). */
export function trophyScale(a: ModAnchors): number {
  // M153: on the 2.6 m bike's thin nose the car-sized cup stood a third of
  // the bike's length tall and wider than the nose under it.
  return a.s * 1.95 * (a.kind === 'bike' ? 0.6 : 1);
}

/** Where the trophy topper's foot stands (pure: the part, the gloves and the
 *  tests share it). M154: on the hood's centreline, clear of every opening
 *  the measured top knows (the anchor stood the monster truck's cup on its
 *  hood mesh and the sports car's against its cowl vents). The bike keeps its
 *  table spot on the nose right ahead of the screen. */
export function trophySpot(a: ModAnchors, surf: SurfaceField | null): { x: number; z: number; r: number; k: number } {
  const r0 = 0.15 * trophyScale(a);
  if (a.kind === 'bike') return { x: a.hood.x, z: a.hood.z, r: r0, k: 1 };
  // M154 r2: no opening BESIDE the foot (with round 1's 3 cm ring the
  // coupe's cup stood between its two hood vents and read as sitting on
  // them): none within TROPHY_FLANK of its radius out from either side of its
  // base; a hood with no such spot near the anchor takes a smaller cup.
  for (const k of TROPHY_FIT) {
    const r = r0 * k;
    const flank = (x: number, z: number): boolean =>
      !surf || [-1, 1].every((sx) => surf.openDistance(x + sx * r, z, TROPHY_FLANK * r + 0.1) >= TROPHY_FLANK * r);
    const m = clearMount(surf, { x: a.hood.x, z: a.hood.z, r, dz: 0.5 * a.k, accept: flank });
    if (m.ok) return { x: m.x, z: m.z, r, k };
    // M154 polish r4: a pickup's cup stays full size between the hood scoop
    // and the nose, its foot's rim touching the scoop's mouth by a few
    // centimetres: the round-5 hood's ramped scoop left no clean ring for
    // it and it shrank to 70% (about half the live cup's height seen from
    // three quarters, behind a corner flag).
    if (k === 1 && a.bed && !flatBedRide(a)) {
      const t = clearMount(surf, { x: a.hood.x, z: a.hood.z, r, dz: 0.5 * a.k, accept: flank, ring: TROPHY_TIGHT_RING });
      if (t.ok) return { x: t.x, z: t.z, r, k };
    }
  }
  const m = clearMount(surf, { x: a.hood.x, z: a.hood.z, r: r0, dz: 0.5 * a.k });
  return { x: m.x, z: m.z, r: r0, k: 1 };
}

/** M154 r2: how far out from each side of the trophy's base (a share of its
 *  radius) no opening may be, and the sizes it may shrink to before
 *  settling for round 1's rule. */
export const TROPHY_FLANK = 0.8;
export const TROPHY_FIT = [1, 0.85, 0.7] as const;
/** M154 polish r4: the ring a pickup's full-size cup stands by when it has
 *  no clean one (negative: its foot's rim may reach that far over an
 *  opening's edge). */
export const TROPHY_TIGHT_RING = -0.035;

/** A footprint a standing part keeps clear of (centre and radius). */
interface Footprint {
  x: number;
  z: number;
  r: number;
}

/** Where the keeper's gloves stand (pure: the part and the tests share it).
 *  M154: on the front fenders, clear of every opening the measured top
 *  knows — the anchor put the coupe's gloves on its louvred hood slots — and
 *  of the trophy topper's foot when it is worn too. Mirrored side to side;
 *  the bike's one glove stands on its nose.
 *  M154 r2: the glove's footprint is its thumb's reach (the thumb stood over
 *  the sports car's cowl slot), with 5 cm of clear paint round it; it keeps
 *  clear of the cup, the floodlight masts and the fans too; and where the nose
 *  has no room beside the cup (the bike's, the Formula's) the gloves stand on
 *  the bike's tail or the Formula's sidepods instead. */
export function gloveSpots(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[]): GloveSpot[] {
  const g = (a.kind === 'bike' ? 0.8 : 1) * a.s;
  const r = GLOVE_REACH * g;
  const ring = GLOVE_RING;
  const avoid: Footprint[] = [];
  if (mods.includes('mod.trophy')) avoid.push(trophySpot(a, surf));
  if (mods.includes('mod.floods')) for (const m of floodSpots(a, surf, mods).spots) avoid.push({ x: m.x, z: m.z, r: 0.17 * a.s });
  if (mods.includes('mod.fans')) {
    for (const f of fanLayout(a, surf, mods)) {
      const v = fanVolume(f);
      avoid.push({ x: v.x, z: v.z, r: v.r });
    }
  }
  if (a.kind === 'bike') {
    const home = [{ sx: 1, x: 0, z: a.fender.z, k: 1 }];
    // the nose, right ahead of the screen, unless another part takes it (the
    // cup stands there too): then the tail's top behind the seat
    const taken = avoid.some((o) => Math.hypot(o.x, o.z - a.fender.z) < o.r + r + ring);
    if (!taken || !surf) return home;
    const tail = clearMount(surf, { x: 0, z: a.tailZ + 0.3 * a.k, r, ring, dz: 0.2 * a.k, maxRise: 0.15, avoid });
    return tail.ok ? [{ sx: 1, x: tail.x, z: tail.z, k: 1 }] : home;
  }
  const fx = Math.abs(a.fender.x);
  const pair = (m: { x: number; z: number }, k = 1): GloveSpot[] => [
    { sx: -1, x: -m.x, z: m.z, k },
    { sx: 1, x: m.x, z: m.z, k },
  ];
  const m = clearMount(surf, { x: fx, z: a.fender.z, r, ring, dx: Math.min(0.15 * a.k, Math.max(0, fx - r - 0.05)), dz: 0.5 * a.k, avoid });
  if (m.ok || a.kind !== 'f1' || !surf) return pair(m);
  // The Formula's narrow nose has no room beside the cup: a smaller pair on
  // the sidepods' tops, between the engine cover and the pods' edges
  // (standing on the cuff, with a 2 cm ring: the pods are narrow).
  const k = 0.75;
  const pod = clearMount(surf, {
    x: Math.abs(a.door.x),
    z: a.door.z,
    r: r * k,
    ring: 0.02,
    support: 0.19 * g * k,
    dx: 0.36 * a.k,
    dz: 0.5 * a.k,
    maxRise: 0.3,
    avoid,
  });
  return pod.ok ? pair(pod, k) : pair(m);
}

/** Where one keeper's glove stands, which side it is (its thumb points out)
 *  and its size (1 = the ride's own). */
export interface GloveSpot {
  sx: number;
  x: number;
  z: number;
  k: number;
}

/** M154 r2: a glove's footprint radius (its thumb's reach, units of its
 *  size) and the clear paint kept round it. */
export const GLOVE_REACH = 0.26;
export const GLOVE_RING = 0.05;

// ---------------------------------------------------------------------------
// Keeper's Gloves — two gloves standing on the front fenders; the fingers
// curl into a grab while the ball rides the nose.
// ---------------------------------------------------------------------------
export const gloves: PartFn = (a, c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const pb = new PartBuilder();
  const pivots: THREE.Group[] = [];
  const spots = gloveSpots(a, opts.surface, opts.mods);
  for (const { sx, x, z, k } of spots) {
    const g = (a.kind === 'bike' ? 0.8 : 1) * s * k;
    // The cuff stands ON the measured panel under it (the bike's spot is the
    // nose just ahead of the screen: the table height, the screen glass is
    // right behind it).
    // (the bike's home spot is its nose right ahead of the screen glass: the
    // table height there; anywhere else the measured body)
    const y = a.kind === 'bike' && z === a.fender.z ? a.fender.y : bodyY(opts, x, z, a.fender.y);
    pb.cyl(0.19 * g, 0.19 * g, 0.14 * g, x, y + 0.07 * g, z, WHITE);
    // (the wrist strap stops just under the cuff's top: flush with it, the two
    // faces fought and flickered black and white, M154 polish)
    pb.box(0.42 * g, 0.05 * g, 0.17 * g, x, y + 0.105 * g, z, DARK, { radius: 0.01 });
    const pz = y + 0.14 * g;
    pb.box(0.36 * g, 0.42 * g, 0.14 * g, x, pz + 0.21 * g, z, c.primary, { rot: [R(10), 0, 0], radius: 0.04 * g, bucket: 'paint' });
    pb.box(0.31 * g, 0.36 * g, 0.035 * g, x, pz + 0.22 * g, z + 0.085 * g, LATEX, { rot: [R(10), 0, 0], radius: 0.01 });
    pb.box(0.08 * g, 0.26 * g, 0.11 * g, x + sx * 0.23 * g, pz + 0.3 * g, z + 0.02 * g, c.primary, {
      rot: [R(10), 0, -sx * R(42)],
      radius: 0.03 * g,
      bucket: 'paint',
    });
    const piv = new THREE.Group();
    piv.position.set(x, pz + 0.42 * g, z + 0.07 * g);
    const fb = new PartBuilder();
    for (const off of [-0.13, -0.045, 0.045, 0.13]) {
      const fan = -off * 2.2 * 0.5;
      fb.box(0.082 * g, 0.3 * g, 0.11 * g, off * g, 0.15 * g, 0, c.primary, { rot: [0, 0, fan], radius: 0.03 * g, bucket: 'paint' });
      fb.box(0.07 * g, 0.27 * g, 0.03 * g, off * g, 0.15 * g, 0.05 * g, LATEX, { rot: [0, 0, fan], radius: 0.008 });
    }
    piv.add(fb.build(m));
    pivots.push(piv);
    group.add(piv);
  }
  group.add(pb.build(m));
  let curl = 0;
  return {
    group,
    update: (ctx, dt): void => {
      curl = damp(curl, ctx.dribbling ? 1 : 0, 8, dt);
      for (const p of pivots) p.rotation.x = 0.17 + curl * 1.35;
    },
  };
};

// ---------------------------------------------------------------------------
// Stadium Floodlights — twin lattice pylons at the rear corners, lamp heads
// aimed forward over the roof; lamps switch on bank by bank, beams fade in.
// ---------------------------------------------------------------------------
/** The floodlight masts' spots and height (pure: the part and the corner
 *  flags share it). Every base plate seats on the highest point of the body
 *  under it. */
export function floodSpots(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[] = [],
): { spots: { sx: number; x: number; y: number; z: number; slope?: number }[]; H: number } {
  const s = a.s;
  const at = (sx: number, x: number, z: number): { sx: number; x: number; y: number; z: number } => ({
    sx,
    x,
    z,
    y: surf?.topIn(x - 0.15 * s, x + 0.15 * s, z - 0.15 * s, z + 0.15 * s) ?? a.deckY,
  });
  if (a.kind === 'bike') {
    // M153: two short masts on the tail's rear corners FRAME the rider from
    // the chase camera (one tall mast in the middle put its lamp head over
    // his back — the player's view of their own bike).
    // M154 polish r4: with the pillion fan aboard too, as live: each lamp
    // head on its own tower's top, the towers on the tail behind him (polish
    // r3 hung the heads out on booms off shorter masts: the near one at his
    // knee, the far one past the tail). Each base seats ON the tail's rounded
    // top under it, tilted to its cross slope (seated on the highest point
    // under the plate, its outer edge stood 6 cm off the round-5 tail).
    const z = a.tailZ + 0.12 * a.k;
    const seat = (sx: number, x: number): { sx: number; x: number; y: number; z: number; slope?: number } => {
      const d = 0.08 * s;
      const yc = surf?.topAt(x, z) ?? null;
      const yl = surf?.topAt(x - d, z) ?? null;
      const yr = surf?.topAt(x + d, z) ?? null;
      if (yc === null || yl === null || yr === null) return at(sx, x, z);
      return { sx, x, z, y: yc, slope: (yr - yl) / (2 * d) };
    };
    return { spots: [seat(-1, -0.13 * a.k), seat(1, 0.13 * a.k)], H: 1.05 * s };
  }
  if (a.kind === 'f1') return { spots: [at(-1, -0.56 * a.k, a.deckZ), at(1, 0.56 * a.k, a.deckZ)], H: 0.9 * s };
  // The rear deck corners, forward of a stock wing (they stood on the
  // coupe's wing tips).
  const z = clearOfWing(a, a.tailZ + 0.36 * s, 0.17 * s);
  const H = 1.35 * s;
  const pair = (zz: number, xx: number): { sx: number; x: number; y: number; z: number }[] => [at(-1, -xx, zz), at(1, xx, zz)];
  if (mods.includes('mod.fans') && a.bed && !flatBedRide(a)) {
    // M154 polish r3: with the fans filling a pickup's bed (four of them,
    // scarves overhead) the masts stood among them from every view: they
    // stand on the cab roof's rear corners instead, shorter, their lamps
    // aimed forward over the roof.
    const zr = a.roofRear.z + 0.16 * s;
    const half = roofHalfAt(surf, zr, 0.05, a.halfW, a.halfW * 0.7);
    return { spots: pair(zr, Math.max(0.2 * s, half - 0.17 * s)), H: FLOOD_ROOF_H * s };
  }
  if (mods.includes('mod.fans') && a.bed) {
    // M154 polish r3: with the fans in the bed or on the deck the masts stood
    // among them (the monster truck's among the seated crew's legs, the
    // pickup's in front of a fan): they move along the bed's sides to the
    // spot with the most room from every fan (fanVolume), the corners first.
    const places = fanLayout(a, surf, mods);
    const fans = places.map(fanVolume);
    // M154 polish r4: the room now asked of a spot is the lamp heads clear of
    // the fans' heads as the cameras see them (FLOOD_VIEWS), and the lattice
    // clear of each fan's body and raised arms in plan (FLOOD_FAN_PLAN). The
    // round-3 room (clear of each fan's whole drum, scarf and all) was found
    // nowhere on the monster truck's deck, so its masts took the "roomiest"
    // spot beside the crew's boots, where the near lamp head covered a fan's
    // head from the three-quarter views.
    const heads = places.map(fanHeadCentre);
    const room = (sp: { x: number; y: number; z: number }[]): number => {
      let r = floodHeadsRoom(sp, H, s, heads) - FLOOD_VIEW_ROOM;
      for (const m of sp) {
        for (const f of fans) {
          if (f.y1 < m.y || f.y0 > m.y + H + 0.32 * s) continue;
          r = Math.min(r, Math.hypot(f.x - m.x, f.z - m.z) - FLOOD_FAN_PLAN * a.k);
        }
      }
      return r;
    };
    // (the nearest spot with room, else the roomiest)
    let best = pair(z, a.halfW * 0.8);
    let bestRoom = room(best);
    if (bestRoom < 0) {
      const zs: number[] = [];
      for (let zz = a.tailZ + 0.16 * s; zz <= a.bed.z1 - 0.12 * s + 1e-9; zz += 0.04) zs.push(zz);
      zs.sort((p, q) => Math.abs(p - z) - Math.abs(q - z));
      // (a base seated level: no tail lip or rail top raising it off the deck)
      const level = (sp: { x: number; y: number; z: number }[]): boolean =>
        !surf ||
        sp.every((m) => {
          let lo = Infinity;
          for (const dx of [-0.12, 0, 0.12]) for (const dz of [-0.12, 0, 0.12]) lo = Math.min(lo, surf.topAt(m.x + dx * s, m.z + dz * s) ?? -Infinity);
          return m.y - lo <= 0.07 * a.k;
        });
      for (const zz of zs) {
        const sp = pair(zz, a.halfW * 0.8);
        if (!level(sp)) continue;
        const r = room(sp);
        if (r >= 0) {
          best = sp;
          break;
        }
        if (r > bestRoom + 0.02) {
          best = sp;
          bestRoom = r;
        }
      }
    }
    return { spots: best, H };
  }
  return { spots: pair(z, a.halfW * 0.8), H };
}

/** M154 polish r3: the masts' height (units of s) on a pickup's cab roof.
 *  (Polish r4: 0.62 left a stub of lattice under each lamp head, the heads
 *  two white boxes over the fans' heads from the three-quarter view; this
 *  tall, the towers read as towers and the heads stand clear above the crew
 *  from the race camera and both three-quarter views.) */
export const FLOOD_ROOF_H = 1.1;

/** M154 polish r4: the directions (car-local, toward the camera) the lamp
 *  heads are kept clear of the fans' heads from: the race camera, and the
 *  garage's and shop's three-quarter views from behind and from ahead. */
export const FLOOD_VIEWS: readonly THREE.Vector3[] = [
  V(0, Math.sin(0.42), -Math.cos(0.42)),
  V(Math.SQRT1_2 * Math.cos(0.55), Math.sin(0.55), -Math.SQRT1_2 * Math.cos(0.55)),
  V(Math.SQRT1_2 * Math.cos(0.4), Math.sin(0.4), -Math.SQRT1_2 * Math.cos(0.4)),
  V(Math.SQRT1_2 * Math.cos(0.36), Math.sin(0.36), Math.SQRT1_2 * Math.cos(0.36)),
];
/** M154 polish r4: the clear gap kept between a lamp head and a fan's head
 *  in those views (metres). */
export const FLOOD_VIEW_ROOM = 0.1;
/** M154 polish r4: the least distance in plan from a mast to a fan beside
 *  it (model units of k): his shoulders, and his raised hands swinging at
 *  his sides, stay clear of the lattice. */
export const FLOOD_FAN_PLAN = 0.4;

/**
 * M154 polish r4 (pure; the tests pin it): the smallest gap, over
 * FLOOD_VIEWS, between a lamp head on a mast `H` tall at each spot (a ball
 * 0.27 s round its centre, 0.12 s over the mast top) and a fan's head, as
 * the camera sees them (negative: they overlap). Both sides are checked by
 * symmetry: the spots come in mirrored pairs.
 */
export function floodHeadsRoom(
  spots: readonly { x: number; y: number; z: number }[],
  H: number,
  s: number,
  heads: readonly { c: THREE.Vector3; r: number }[],
): number {
  let gap = Infinity;
  const up = V(0, 1, 0);
  for (const d of FLOOD_VIEWS) {
    const right = up.clone().cross(d).normalize();
    const vup = d.clone().cross(right).normalize();
    for (const m of spots) {
      const l = V(m.x, m.y + H + 0.12 * s, m.z);
      for (const h of heads) {
        const dx = l.dot(right) - h.c.dot(right);
        const dy = l.dot(vup) - h.c.dot(vup);
        gap = Math.min(gap, Math.hypot(dx, dy) - 0.27 * s - h.r);
      }
    }
  }
  return gap;
}

/** A bed whose floor is the deck (the monster truck's), not a tub. */
function flatBedRide(a: ModAnchors): boolean {
  return !!a.bed && a.bed.floorY >= a.deckY - 0.02 * a.k;
}

export const floods: PartFn = (a, _c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const pb = new PartBuilder();
  const { spots, H } = floodSpots(a, opts.surface, opts.mods);
  const banks: THREE.Group[] = [];
  const cones: THREE.MeshBasicMaterial[] = [];
  for (const { sx, x, y, z, slope = 0 } of spots) {
    // (the base plate lies on the body under it, tilted to its cross slope
    // where floodSpots measured one: the bike's rounded tail top)
    const roll = Math.atan(slope);
    pb.box(0.3 * s, 0.04 * s, 0.3 * s, x, y + 0.02 * s, z, DARK, { radius: 0.008, rot: [0, 0, roll] });
    const legs: [THREE.Vector3, THREE.Vector3][] = [];
    for (const [cx, cz] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as const) {
      const p0 = V(x + cx * 0.11 * s, y + slope * cx * 0.11 * s + 0.04 * s, z + cz * 0.11 * s);
      const p1 = V(x + cx * 0.05 * s, y + H, z + cz * 0.05 * s);
      pb.rod(p0, p1, 0.018 * s, GALV);
      legs.push([p0, p1]);
    }
    for (const lv of [0.3, 0.6, 0.85]) {
      const ring = legs.map(([p0, p1]) => p0.clone().lerp(p1, lv));
      const nxt = legs.map(([p0, p1]) => p0.clone().lerp(p1, Math.min(1, lv + 0.25)));
      for (let i = 0; i < 4; i++) {
        const b = ring[(i + 1) % 4];
        const n = nxt[(i + 1) % 4];
        if (ring[i] && b) pb.rod(ring[i] as THREE.Vector3, b, 0.011 * s, GALV);
        if (ring[i] && n) pb.rod(ring[i] as THREE.Vector3, n, 0.009 * s, GALV);
      }
    }
    pb.box(0.52 * s, 0.03 * s, 0.34 * s, x, y + H, z, GALV, { radius: 0.005 });
    const hz = y + H + 0.12 * s;
    const q = yawTilt(-sx * R(14), R(32));
    pb.box(0.5 * s, 0.34 * s, 0.14 * s, x, hz, z, WHITE, { rot: q, radius: 0.012 });
    const face = V(0, 0, 0.065 * s).applyQuaternion(q);
    pb.box(0.46 * s, 0.3 * s, 0.03 * s, x + face.x, hz + face.y, z + face.z, DARK, { rot: q, radius: 0.006 });
    const lb = new PartBuilder();
    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 2; j++) {
        const o = V((-0.5 + i) * 0.2 * s, (-0.5 + j) * 0.14 * s, 0.075 * s).applyQuaternion(q);
        lb.box(0.16 * s, 0.11 * s, 0.02 * s, x + o.x, hz + o.y, z + o.z, LAMP, { rot: q, bucket: 'glow', radius: 0.004 });
      }
    }
    const bank = lb.build(m);
    bank.visible = false;
    banks.push(bank);
    group.add(bank);
    const dir = V(0, 0, 1).applyQuaternion(q);
    const cm = coneMaterial(m, 0xfff1cc);
    const cone = new THREE.Mesh(lightCone(V(x, hz, z), dir, 1.5 * s, 8 * s), cm);
    cone.frustumCulled = false;
    cones.push(cm);
    group.add(cone);
  }
  group.add(pb.build(m));
  let age = 0;
  return {
    group,
    update: (ctx, dt): void => {
      age += dt;
      banks.forEach((b, i) => {
        b.visible = age > 0.45 + i * 0.3;
      });
      const on = clamp01((age - 0.5) / 0.8);
      for (const cm of cones) cm.opacity = 0.045 * on;
      group.rotation.z = damp(group.rotation.z, clamp(-ctx.lateralSpeed * 0.004, -0.06, 0.06), 6, dt);
    },
  };
};

/** M154 polish r2: the gap kept between the Formula's pod number and a glove
 *  standing on the pod, or the front of the number and the end of the cape's
 *  resting cloth. */
export const POD_NUMBER_GAP = 0.03;

/** M154 polish r3 (pure; the tests pin it): how far the Formula's pod
 *  number fades for a camera whose direction from it runs `dz` toward the
 *  nose (car-local, unit vector): none from behind or square from the side,
 *  gone from the front quarter. */
export function podNumberAhead(dz: number): number {
  return clamp01((dz - 0.12) / 0.2);
}

/**
 * M154 polish r2 (pure; the part and the tests share it): where the Formula's
 * Kit Number lies on its sidepod tops, +X pod first (the -X one mirrors it).
 * At the anchor, as large as the flat paint allows (round 1's rule), unless
 * something else takes that stretch of pod:
 *  - the keeper's gloves, which stand on the pods when the nose has no room:
 *    they stood on the numbers;
 *  - the cape's resting cloth, whose edges overhang the pods' inner edges:
 *    from the race camera it cut the numbers' inner digits (round 1 hid the
 *    numbers under a resting cape, so a buyer of both saw none).
 * Then the number moves aft along the pod, smaller if the pod narrows there,
 * until nothing sits on it and the cloth ends in front of it. Null without a
 * surface (the part lays the anchor's 0.8 size).
 */
export function podNumberSpots(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[],
): { x: number; z: number; w: number; l: number }[] | null {
  const at = a.kitNumberAt;
  if (!at || !surf) return null;
  const pw = 0.42 * a.s;
  const pl = pw * (384 / 512) * TOP_DECAL_STRETCH;
  const gap = POD_NUMBER_GAP;
  const gloves = mods.includes('mod.gloves') ? gloveSpots(a, surf, mods) : [];
  const g = (a.kind === 'bike' ? 0.8 : 1) * a.s;
  // the cloth's rear end over the pod (the cape lies along the engine cover
  // and over the pods' inner edges, and does not hang on this car)
  let clothEnd = Infinity;
  if (mods.includes('mod.tifo')) {
    const shape = capeShape(surf, tifoSpec(a, surf, mods), (x, z) => restTopAt(a, null, x, z, true));
    const x0 = at.top.x - pw / 2;
    const x1 = at.top.x + pw / 2;
    for (let z = a.tailZ; z <= a.noseZ; z += 0.01) {
      let over = false;
      for (let x = x0; x <= x1 + 1e-9 && !over; x += 0.02) over = shape.restAt(x, z) !== null;
      if (over) {
        clothEnd = z;
        break;
      }
    }
  }
  const taken = (s: { x: number; z: number; w: number; l: number }): boolean => {
    if (s.z + s.l / 2 > clothEnd - gap) return true;
    for (const gl of gloves) {
      const r = GLOVE_REACH * g * gl.k + gap;
      const nx = Math.max(s.x - s.w / 2, Math.min(gl.x, s.x + s.w / 2));
      const nz = Math.max(s.z - s.l / 2, Math.min(gl.z, s.z + s.l / 2));
      if (Math.hypot(gl.x - nx, gl.z - nz) < r) return true;
    }
    return false;
  };
  const free = !gloves.length && clothEnd === Infinity;
  const out: { x: number; z: number; w: number; l: number }[] = [];
  for (const sx of [1, -1]) {
    const x = sx * at.top.x;
    if (!free && out[0]) {
      // the pair stays mirrored when the other pod takes the same spot (a
      // few centimetres of asymmetry in the mesh moved one number 6 cm aft)
      const p = out[0];
      const k = p.w / pw;
      const twin = topDecalSpot(surf, -p.x, p.z, pw * k, pl * k, { dx: 0, dz: 0 }, [1], (s) => !taken(s));
      if (twin) {
        out.push(twin);
        continue;
      }
    }
    const spot = free
      ? topDecalSpot(surf, x, at.top.z, pw, pl)
      : (topDecalSpot(surf, x, at.top.z, pw, pl, { dx: 0.08, dz: 0.8 }, undefined, (s) => !taken(s)) ??
        topDecalSpot(surf, x, at.top.z, pw, pl));
    if (!spot) return null;
    out.push(spot);
  }
  return out;
}

/**
 * M154 polish r3 (pure; the part and the tests share it): the centre z of
 * the Kit Number lying on the roof, `h` long. At the roof point, but kept on
 * the roof (the monster truck's hung 5 cm past the roof's rear edge, its
 * feet in the air) and forward of a worn cape's rail when the number lies
 * ahead of it (the monster truck's cape now hangs from the roof's rear edge:
 * its rail and the cloth's first row covered the digits' feet from the race
 * camera). A number the cloth lies over (the open muscle car's deck) stays.
 */
export function roofNumberZ(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[], h: number): number {
  let z = a.roof.z;
  const gap = 0.03 * a.k;
  if (a.kind !== 'car') return z;
  if (a.roofFront.z - a.roofRear.z > h + 2 * gap) z = Math.max(z, a.roofRear.z + h / 2 + gap);
  if (mods.includes('mod.tifo')) {
    const t = tifoSpec(a, surf, mods);
    // (the rail's bar and clasps reach this far forward of its centre)
    const railFront = t.anchor.z + 0.05 * a.s;
    if (z > t.anchor.z && z - h / 2 < railFront + gap) z = railFront + gap + h / 2;
  }
  return z;
}

/**
 * M154 polish r5 (pure; the part and the tests share it): the bike's side
 * Kit Number: the digits' height (the live build's: its decal 0.3 s tall,
 * the digits the INK of it, so a decal cropped to the ink carries them at
 * live size on a smaller patch of paint; BIKE_NUMBER_GROW over live), the
 * spot it is searched round (the live build's, behind the seat on the
 * tail's upper flank) and how far, and how close behind the flank non-paint
 * geometry may sit (flankDecalSpot): 2.5 cm, so the number stays whole on the
 * tail's RED band and never runs onto the owner-approved dark lower side
 * panel, where its dark outline vanished (round 6: 74% of live's white, half
 * of it on the dark panel).
 */
export function bikeSideNumber(a: ModAnchors): { h: number; z: number; y: number; reach: { dz: number; dy: number }; paintDepth: number; maxLean: number } {
  return {
    h: 0.3 * a.s * BIKE_NUMBER_GROW,
    z: a.tailZ + 0.35 * a.k,
    y: a.deckY - 0.1 * a.k,
    reach: { dz: 0.3 * a.k, dy: 0.12 * a.k },
    paintDepth: 0.025,
    // (upright: the tail's flank rolls inward toward its top, more so going
    // forward; on that roll the digits leaned from the race camera's angle)
    maxLean: 0.02,
  };
}
/** The bike's side number over the live build's size. */
export const BIKE_NUMBER_GROW = 1.1;

/** (pure; the tests pin it) A decal cropped to its ink: the patch (w x h,
 *  metres) that carries digits drawn on a full decal `h` tall. */
export function inkDecalSize(h: number, ink: InkBox): { w: number; h: number } {
  return { w: h * (512 / 384) * (ink.u1 - ink.u0), h: h * (ink.v1 - ink.v0) };
}

// ---------------------------------------------------------------------------
// Kit Number — the picked number, jersey style, on the roof, the tail and
// both doors (the bike's tail top + cowl flanks, the Formula's sidepod tops
// and rear-wing end plates).
// ---------------------------------------------------------------------------
export const kitnumber: PartFn = (a, _c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const map = m.trackTex(numberTexture(opts.kitNumber));
  const mat = m.track(
    new THREE.MeshBasicMaterial({
      map,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
    }),
  );
  const decal = (h: number, p: THREE.Vector3, q: THREE.Quaternion): THREE.Mesh => {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(h * (512 / 384), h), mat);
    mesh.position.copy(p);
    mesh.quaternion.copy(q);
    mesh.castShadow = false;
    group.add(mesh);
    return mesh;
  };
  const Y = V(0, 1, 0);
  const qBack = new THREE.Quaternion().setFromAxisAngle(Y, Math.PI);
  const surf = opts.surface;
  // A decal lying ON the measured body at (x, z): tilted to the panel's own
  // slope, the digit's top toward the nose so it reads from the chase camera.
  const lying = (h: number, x: number, z: number, fallbackY: number): void => {
    const d = 0.06;
    const y = bodyY(opts, x, z, fallbackY);
    const n = V(0, 1, 0);
    if (surf) {
      const xl = surf.topAt(x - d, z);
      const xr = surf.topAt(x + d, z);
      const zf = surf.topAt(x, z + d);
      const zb = surf.topAt(x, z - d);
      if (xl !== null && xr !== null && zf !== null && zb !== null) n.set(-(xr - xl) / (2 * d), 1, -(zf - zb) / (2 * d)).normalize();
    }
    const up = V(0, 0, 1).addScaledVector(n, -n.z).normalize();
    const right = up.clone().cross(n);
    const q = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(right, up, n));
    decal(h, V(x, y, z).addScaledVector(n, 0.012), q);
  };
  // The tail decal on the face the chase camera sees: the rearmost point of
  // the body behind that spot (the probe's tail points sit on, or inside, it).
  const tail = (h: number, p: THREE.Vector3): THREE.Mesh => {
    const z = surf?.rearZ(p.x - 0.12, p.x + 0.12, p.y - 0.05, p.y + 0.05) ?? p.z - 0.03;
    return decal(h, V(p.x, p.y, z - 0.012), qBack);
  };
  // Door decals ON the measured flank (M154): a patch of unbroken paint
  // near the door point (no arch, scoop, vent or handle under it), the decal
  // a grid bent to the flank's envelope 1.2 cm proud — the M128 plane, set
  // from the flank's extent near its centre only, was cut by the coupe's
  // shoulder crease and lower door bulge.
  // M154 r2: the decal is one smooth surface fitted to the flank (it bent
  // with every cell of it and the digits warped from a three-quarter view),
  // and keeps off anything worn on the flank (side-exit whistles, the cleat
  // plates).
  const avoid: FlankBox[] = [];
  if (opts.mods.includes('mod.whistle') && a.exhaust?.dir === 'left') {
    for (const b of whistleBounds(a, surf, opts.mods)) avoid.push({ z0: b.z0, z1: b.z1, y0: b.y0, y1: b.y1 });
  }
  if (opts.mods.includes('mod.cleats')) avoid.push(cleatBox(a, surf));
  const doors = (h: number, p: THREE.Vector3, step?: number, reach?: { dz: number; dy: number }, paintDepth?: number): void => {
    const spot = flankDecalSpot(surf, p.z, p.y, h * (512 / 384), h, reach, avoid, paintDepth, step);
    for (const side of [1, -1] as const) {
      const fallbackX = surf?.sideX(spot.z - spot.w / 2, spot.z + spot.w / 2, spot.y - spot.h / 2, spot.y + spot.h / 2) ?? p.x + 0.033;
      const mesh = new THREE.Mesh(flankDecalGeometry(surf, side, spot.z, spot.y, spot.w, spot.h, fallbackX), mat);
      mesh.castShadow = false;
      group.add(mesh);
    }
  };
  if (a.kind === 'f1' && a.kitNumberAt) {
    // M154 polish: the Formula has no roof, tail face or door for its number,
    // and the M153 mounts (the fin's flanks inside the engine-cover tent, and
    // a pair either side of it) read as a lone '0' and a half-buried '10'
    // across the gully. Now the number LIES ON EACH SIDEPOD TOP — the one
    // broad flat paint the race camera looks down on — fitted to the measured
    // top (one smooth surface, never cut, never standing off) and drawn a
    // little long so it reads in proportion from the chase view; and it
    // STANDS ON THE REAR WING'S END PLATES, flat panels the side and
    // three-quarter views see whole.
    const at = a.kitNumberAt;
    const pw = 0.42 * s;
    const pl = pw * (384 / 512) * TOP_DECAL_STRETCH;
    // M154 polish r2: the pods' numbers keep off the gloves and out from
    // under the cape's resting cloth (podNumberSpots), and each is drawn
    // only when the camera sees it WHOLE (decalSeenShare): from the rear
    // three-quarter view the engine cover hid the far pod's '1' and left a
    // lone '0' (the owner's original complaint); that view reads the number
    // on the near pod and the rear wing's end plates.
    const spots = podNumberSpots(a, surf, opts.mods) ?? [1, -1].map((sx) => ({ x: sx * at.top.x, z: at.top.z, w: 0.8 * pw, l: 0.8 * pl }));
    let clothTop: ((x: number, z: number) => number | null) | null = null;
    const inv = new THREE.Matrix4();
    const cam = new THREE.Vector3();
    const dir = new THREE.Vector3();
    for (const spot of spots) {
      const own = m.track(mat.clone());
      const mesh = new THREE.Mesh(topDecalGeometry(surf, spot.x, spot.z, spot.w, spot.l, at.top.y), own);
      mesh.castShadow = false;
      group.add(mesh);
      // points over the digits (the texture's ink spans the middle of the
      // decal), just over the measured top
      const pts: THREE.Vector3[] = [];
      for (const fx of [-0.36, -0.18, 0, 0.18, 0.36]) {
        for (const fz of [-0.3, -0.1, 0.1, 0.3]) {
          const x = spot.x + fx * spot.w;
          const z = spot.z + fz * spot.l;
          pts.push(V(x, (surf?.topAt(x, z) ?? at.top.y) + 0.012, z));
        }
      }
      mesh.onBeforeRender = (_r, _s, camera): void => {
        if (!surf || !mesh.parent) return;
        inv.copy(mesh.parent.matrixWorld).invert();
        const ortho = (camera as THREE.OrthographicCamera).isOrthographicCamera;
        if (ortho) camera.getWorldDirection(dir).negate().transformDirection(inv);
        else cam.setFromMatrixPosition(camera.matrixWorld).applyMatrix4(inv);
        // (a perspective camera: the direction from the decal's centre)
        if (!ortho) dir.copy(cam).sub(V(spot.x, at.top.y, spot.z)).normalize();
        // (M154 polish r3: and not from AHEAD of the car: lying on the pod
        // top to read from the race camera behind, seen from the front
        // quarter it was upside down and nearly edge on, a white scribble)
        own.opacity = decalSeenOpacity(decalSeenShare(surf, pts, dir, 0.9, clothTop)) * (1 - podNumberAhead(dir.z));
      };
    }
    const ph = 0.25 * s;
    const plate = flankDecalSpot(surf, at.side.z, at.side.y, ph * (512 / 384), ph, { dz: 0.08, dy: 0.04 }, avoid, 0.012);
    for (const side of [1, -1] as const) {
      const fallbackX = surf?.sideX(plate.z - plate.w / 2, plate.z + plate.w / 2, plate.y - plate.h / 2, plate.y + plate.h / 2) ?? at.side.x;
      const mesh = new THREE.Mesh(flankDecalGeometry(surf, side, plate.z, plate.y, plate.w, plate.h, fallbackX), mat);
      mesh.castShadow = false;
      group.add(mesh);
    }
    return {
      group,
      update: (ctx): void => {
        // (the cape's cloth where it is now: it stands between a camera in
        // front of the car and the numbers behind it)
        clothTop = ctx.capeTopAt ?? null;
      },
    };
  }
  if (a.kind === 'bike') {
    // M153: the raised tail's flat top (what the chase camera looks down on)
    // and the cowl's flat flanks — the M128 decals were a fleck under the
    // exhausts and a sliver on the lower fairing.
    const z = a.tailZ + 0.3 * a.k;
    lying(0.42 * s, 0, z, a.deckY);
    // M154 polish r5: the side number is cropped to its digits' ink and
    // kept whole on the red band (bikeSideNumber)
    const side = bikeSideNumber(a);
    const ink = numberInk(opts.kitNumber);
    const box = inkDecalSize(side.h, ink);
    const spot = flankDecalSpot(surf, side.z, side.y, box.w, box.h, side.reach, avoid, side.paintDepth, 0.01, side.maxLean);
    for (const sd of [1, -1] as const) {
      const fallbackX = surf?.sideX(spot.z - spot.w / 2, spot.z + spot.w / 2, spot.y - spot.h / 2, spot.y + spot.h / 2) ?? 0.2 * a.k;
      const geo = flankDecalGeometry(surf, sd, spot.z, spot.y, spot.w, spot.h, fallbackX, undefined, 16, 12, true);
      const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
      for (let i = 0; i < uv.count; i++) uv.setXY(i, ink.u0 + uv.getX(i) * (ink.u1 - ink.u0), ink.v0 + uv.getY(i) * (ink.v1 - ink.v0));
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = false;
      group.add(mesh);
    }
    return { group };
  }
  lying(0.6 * s, a.roof.x, roofNumberZ(a, surf, opts.mods, 0.6 * s), a.roof.y);
  const th = 0.32 * s;
  const tp = a.tailNumber ?? a.tailPanel;
  const tailMesh = tail(th, tp);
  doors(0.38 * s, a.door);
  // M154: under a resting cape the tail number hides (the raised hem crossed
  // it and its foot peeked out below); it shows when the cape flies.
  const tw = (th * (512 / 384)) / 2;
  return {
    group,
    update: (ctx): void => {
      const c = ctx.capeTail;
      const covered = !!c && c.lift < 0.7 && tp.x + tw > c.x0 && tp.x - tw < c.x1 && tp.y + th / 2 > c.y0 && tp.y - th / 2 < c.y1;
      tailMesh.visible = !covered;
    },
  };
};

// ---------------------------------------------------------------------------
// Cleat Sills — boot-sole side plates with oversized studs and a heel plate
// under the rear bumper; the outer studs throw sparks in hard corners.
// ---------------------------------------------------------------------------
/** Where the cleat plates run along the car (pure: the plates and the door
 *  number share it). */
function cleatRun(a: ModAnchors): [number, number] {
  // M153: the bike's run between the wheels under the pegs (the probe's sill
  // on the bike is the front of the belly, by the front tyre); a car's the
  // whole rocker, arch to arch (on the muscle car the probe's sill stopped
  // 0.4 m short of the rear wheel).
  if (a.kind === 'bike') return [a.wheelZ + a.wheelR + 0.06 * a.k, a.sillZ[0] - 0.05 * a.k];
  return [Math.min(a.sillZ[0], a.wheelZ + a.wheelR * 1.15 + 0.03 * a.k), a.sillZ[1]];
}

/** M154 polish r2 (pure; the tests pin it): the stretch of z0..z1 where the
 *  measured flank reaches `inner` (|x|, within 2.5 cm) in the band round
 *  height `y`: the longest such run, or the whole run when nothing is
 *  measured or the run found is shorter than a fifth of it. */
export function bikeCleatSpan(surf: SurfaceField | null, z0: number, z1: number, y: number, inner: number): [number, number] {
  if (!surf) return [z0, z1];
  const step = 0.02;
  let best: [number, number] | null = null;
  let start = NaN;
  // (M154 polish r3: by index, with one step past the end that always closes
  // an open run: stepping z by 0.02 drifted past the end before that step,
  // so a run reaching the end was dropped and the plate ran the whole length
  // on past the collector)
  const n = Math.floor((z1 - z0) / step + 1e-9);
  for (let i = 0; i <= n + 1; i++) {
    const z = Math.min(z1, z0 + i * step);
    const v = i <= n ? surf.sideX(z - step / 2, z + step / 2, y - 0.05, y + 0.05) : null;
    const ok = v !== null && v >= inner - 0.025;
    if (ok && Number.isNaN(start)) start = z;
    if (!ok && !Number.isNaN(start)) {
      const end = Math.min(z1, z0 + (i - 1) * step);
      if (!best || end - start > best[1] - best[0]) best = [start, end];
      start = NaN;
    }
  }
  if (!best || best[1] - best[0] < 0.2 * (z1 - z0)) return [z0, z1];
  return best;
}

/** M154 polish r5: a bike cleat plate's stretch keeps one width while the
 *  belly's width under it stays within this (metres)... */
export const CLEAT_STEP = 0.04;
/** ...and is at least this long (a shorter one joins its neighbour). */
export const CLEAT_MIN_SEG = 0.08;
/** The angled piece across a step runs this far either side of it. */
export const CLEAT_TURN = 0.04;

/**
 * M154 polish r5 (pure; the tests pin it): the bike's cleat plate as
 * straight stretches along the belly over z0..z1 in the band round height
 * `y`, each with the belly's outermost |x| over it (so the plate's inner
 * face touches the body all along, never sunk into a wider part): a new
 * stretch where the width steps by more than CLEAT_STEP (the bike's belly
 * meets its narrower collector guard). Where nothing is measured at the
 * run's ends the plate stops (never in mid-air); no measure at all = one
 * stretch at `fallback`.
 */
export function bikeCleatSegments(surf: SurfaceField | null, z0: number, z1: number, y: number, fallback: number): { z0: number; z1: number; x: number }[] {
  if (!surf) return [{ z0, z1, x: fallback }];
  const step = 0.02;
  const n = Math.max(1, Math.round((z1 - z0) / step));
  const xs: (number | null)[] = [];
  for (let i = 0; i <= n; i++) {
    const z = z0 + ((z1 - z0) * i) / n;
    xs.push(surf.sideX(z - step / 2, z + step / 2, y - 0.05, y + 0.05));
  }
  let a = 0;
  while (a <= n && xs[a] === null) a++;
  let b = n;
  while (b >= a && xs[b] === null) b--;
  if (a > b) return [{ z0, z1, x: fallback }];
  const zAt = (i: number): number => z0 + ((z1 - z0) * i) / n;
  const segs: { i0: number; i1: number; lo: number; hi: number }[] = [];
  for (let i = a; i <= b; i++) {
    const v = xs[i];
    const cur = segs[segs.length - 1];
    if (v === null || v === undefined) continue;
    if (cur && Math.max(cur.hi, v) - Math.min(cur.lo, v) <= CLEAT_STEP) {
      cur.i1 = i;
      cur.lo = Math.min(cur.lo, v);
      cur.hi = Math.max(cur.hi, v);
    } else segs.push({ i0: i, i1: i, lo: v, hi: v });
  }
  // short stretches join the neighbour they differ least from
  for (let k = 0; k < segs.length && segs.length > 1; ) {
    const sg = segs[k]!;
    if (zAt(sg.i1) - zAt(sg.i0) >= CLEAT_MIN_SEG) {
      k++;
      continue;
    }
    const prev = segs[k - 1];
    const next = segs[k + 1];
    const into = !next || (prev && Math.abs(prev.hi - sg.hi) <= Math.abs(next.hi - sg.hi)) ? prev! : next;
    into.i0 = Math.min(into.i0, sg.i0);
    into.i1 = Math.max(into.i1, sg.i1);
    into.lo = Math.min(into.lo, sg.lo);
    into.hi = Math.max(into.hi, sg.hi);
    segs.splice(k, 1);
    if (into === prev) k = Math.max(0, k - 1);
  }
  // each stretch reaches to the middle of the step to the next
  return segs.map((sg, k) => ({
    z0: k === 0 ? zAt(sg.i0) : (zAt(segs[k - 1]!.i1) + zAt(sg.i0)) / 2,
    z1: k === segs.length - 1 ? zAt(sg.i1) : (zAt(sg.i1) + zAt(segs[k + 1]!.i0)) / 2,
    x: sg.hi,
  }));
}

/** M154 r2: the cleat plates' box on the flank (along z, up y, studs and
 *  welt included): the door number keeps off it. */
export function cleatBox(a: ModAnchors, _surf: SurfaceField | null): { z0: number; z1: number; y0: number; y1: number } {
  const s = a.s;
  const [z0, z1] = cleatRun(a);
  if (a.kind === 'bike') {
    const y = a.sillY - 0.02 * a.k;
    return { z0, z1, y0: y - 0.15 * s, y1: y + 0.07 * s };
  }
  const y = a.sillY + 0.09 * s;
  return { z0, z1, y0: y - 0.17 * s, y1: y + 0.1 * s };
}

export const cleats: PartFn = (a, c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const pb = new PartBuilder();
  const surf = opts.surface;
  const [z0, z1] = cleatRun(a);
  const span = z1 - z0;
  const zm = (z0 + z1) / 2;
  /** The body's half-width along the plate's run at a height band: the
   *  median of the measured flank (a side-exit pipe or a flare is not the
   *  rocker). */
  const rocker = (y0: number, y1: number, fallback: number): number => {
    if (!surf) return fallback;
    const xs: number[] = [];
    for (let i = 0; i <= 12; i++) {
      const z = z0 + (span * i) / 12;
      const v = surf.sideX(z - 0.03, z + 0.03, y0, y1);
      if (v !== null) xs.push(v);
    }
    if (xs.length < 4) return fallback;
    xs.sort((p, q) => p - q);
    return xs[Math.floor(xs.length / 2)]!;
  };
  // A stud is a short cone whose BASE sits on the plate and whose tip points
  // out and down like a boot's — `spike()` takes the tip, so the base is the
  // tip walked back along the direction (the first cut placed the base out
  // in space and left the tips on the road, measured).
  const stud = (base: THREE.Vector3, dir: THREE.Vector3, len: number, r: number): void => {
    const d = dir.clone().normalize();
    const tip = base.clone().addScaledVector(d, len);
    pb.spike(tip, d.clone().negate(), r, len, STUD, 'metal');
    pb.sphere(r * 0.42, tip.x, tip.y, tip.z, c.secondary, { bucket: 'paint' });
  };
  if (a.kind === 'bike') {
    // Low on the belly, just under the rider's boots, against the measured
    // flank (the M128 plates sat INSIDE the fairing — only stud tips showed).
    const y = a.sillY - 0.02 * a.k;
    const w = 0.07 * s;
    // M154 polish r5: the plate runs the whole run again (live's length and
    // four studs) and follows the underside it hangs on: one straight piece
    // per stretch of the belly that keeps one width (bikeCleatSegments), each
    // against its own stretch, joined across the step (round 5 stopped the
    // plate where the narrower dark collector guard begins: half live's
    // length, two studs).
    const segs = bikeCleatSegments(surf, z0, z1, y, 0.2 * a.k);
    const r0 = segs[0]!.z0;
    const r1 = segs[segs.length - 1]!.z1;
    const bs = r1 - r0;
    const n = Math.max(2, Math.min(4, Math.round(bs / (0.2 * a.k))));
    // (the step between two stretches is a short angled piece over
    // CLEAT_TURN either side of it)
    const t = CLEAT_TURN;
    const xOf = (sg: { x: number }): number => sg.x + w / 2 - 0.005;
    for (const sx of [-1, 1]) {
      segs.forEach((sg, i) => {
        const prev = segs[i - 1];
        const next = segs[i + 1];
        const za = sg.z0 + (prev ? t : 0);
        const zb = sg.z1 - (next ? t : 0);
        const x = sx * xOf(sg);
        if (zb > za) {
          pb.box(w, 0.1 * s, zb - za, x, y, (za + zb) / 2, SOLE, { radius: 0.02 * s });
          pb.box(w + 0.01 * s, 0.025 * s, zb - za, x, y + 0.05 * s, (za + zb) / 2, c.primary, { bucket: 'paint', radius: 0.006 });
        }
        if (next) {
          const dx = sx * (xOf(next) - xOf(sg));
          const len = Math.hypot(dx, 2 * t);
          const rot: [number, number, number] = [0, Math.atan2(dx, 2 * t), 0];
          const xm = x + dx / 2;
          pb.box(w, 0.1 * s, len, xm, y, sg.z1, SOLE, { radius: 0.02 * s, rot });
          pb.box(w + 0.01 * s, 0.025 * s, len, xm, y + 0.05 * s, sg.z1, c.primary, { bucket: 'paint', radius: 0.006, rot });
        }
      });
      for (let i = 0; i < n; i++) {
        const z = r0 + (bs * (i + 0.5)) / n;
        const sg = segs.find((q) => z <= q.z1 + 1e-9) ?? segs[segs.length - 1]!;
        const x = sx * (sg.x + w / 2 - 0.005);
        stud(V(x + sx * 0.03 * s, y - 0.03 * s, z), V(sx * 0.8, -0.6, 0), 0.11 * s, 0.05 * s);
      }
    }
  } else {
    const w = 0.15 * s;
    const y = a.sillY + 0.09 * s;
    // M153: the plate's inner face ON the measured rocker (halfW is the
    // widest point of the body — a haunch — so the M128 plates jutted out
    // like running boards).
    // (Sunk 2.5 cm into the rocker: against a dark carbon skirt a plate that
    // only touched it read as floating beside the car.)
    const xo = rocker(a.sillY, a.sillY + 0.18 * s, a.halfW + 0.01) + w / 2 - 0.025;
    for (const sx of [-1, 1]) {
      const x = sx * xo;
      // The sole plate hugs the rocker: a white slab with the kit-colour welt.
      pb.box(w, 0.17 * s, span, x, y, zm, SOLE, { radius: 0.035 * s });
      pb.box(w + 0.01 * s, 0.035 * s, span, x, y + 0.075 * s, zm, c.primary, { bucket: 'paint', radius: 0.008 });
      for (let i = 0; i < 6; i++) {
        const z = z0 + (span * (i + 0.5)) / 6;
        // Studs leave the plate's lower outer edge, angled 35 degrees down.
        stud(V(x + sx * 0.06 * s, y - 0.05 * s, z), V(sx * Math.cos(R(35)), -Math.sin(R(35)), 0), 0.13 * s, 0.055 * s);
      }
    }
  }
  if (a.kind === 'car' && !a.diffuser) {
    // The heel plate across the tail with three studs raked back, its front
    // face against the measured tail (it floated a hand behind the bumper).
    // Not on a ride with a shaped diffuser (the coupe, the sports car): it
    // hid the owner's diffuser from the chase camera; not on the bike or the
    // Formula.
    const hx = a.halfW * 0.55;
    const ty = a.sillY + 0.11 * s;
    const face = surf?.rearZ(-hx, hx, ty - 0.08 * s, ty + 0.08 * s) ?? a.tailZ;
    const tz = face - 0.1 * s + 0.01;
    pb.box(hx * 2, 0.16 * s, 0.2 * s, 0, ty, tz, SOLE, { radius: 0.035 * s });
    pb.box(hx * 2 + 0.02 * s, 0.04 * s, 0.21 * s, 0, ty + 0.07 * s, tz, c.primary, { bucket: 'paint', radius: 0.008 });
    for (let i = 0; i < 3; i++) {
      const x = -hx * 0.65 + (hx * 1.3 * i) / 2;
      stud(V(x, ty - 0.05 * s, tz - 0.09 * s), V(0, -0.55, -0.84), 0.13 * s, 0.055 * s);
    }
  }
  group.add(pb.build(m));
  const sparks = new SpritePool(8, 0xffb060, 0.12 * s, m);
  group.add(sparks.group);
  let emitClock = 0;
  return {
    group,
    update: (ctx, dt): void => {
      const lat = ctx.lateralSpeed;
      emitClock -= dt;
      if (Math.abs(lat) > 5 && ctx.speed > 12 && emitClock <= 0) {
        emitClock = 0.05;
        const side = Math.sign(lat) || 1;
        sparks.emit(
          V(side * (a.halfW + 0.3 * s), a.sillY, z0 + Math.random() * span),
          V(side * (2 + Math.random() * 2), 1.5 + Math.random() * 2, -4 - Math.random() * 4),
          0.3,
          0.8,
        );
      }
      sparks.update(dt, 14, 2.5, 0.12 * s);
    },
  };
};

// ---------------------------------------------------------------------------
// Whistle Pipes — two big brass referee whistles as the exhaust, a lanyard
// slung between them; every boost blows them (a puff from each mouth).
// ---------------------------------------------------------------------------
/** The whistles' size on a ride (the bike's and the Formula's are smaller). */
export function whistleScale(a: ModAnchors): number {
  // M154 polish r2: a car whose real tips are a close CENTRE pair (the
  // sports car's) wears smaller whistles: pushed apart to clear each other
  // there, two full-size bowls filled the middle of its tail and read
  // oversized from the race camera.
  const centrePair = a.kind === 'car' && !!a.exhaust && a.exhaust.dir === 'back' && a.exhaust.pts.every((p) => Math.abs(p.x) < 0.15 * a.s);
  return (a.kind === 'bike' ? 0.65 : a.kind === 'f1' ? 0.85 : centrePair ? WHISTLE_CENTRE_PAIR : 1) * a.s;
}

/** M154 polish r2: the whistles' size on a car with a centre pair of tips. */
export const WHISTLE_CENTRE_PAIR = 0.75;

/** Where the two whistles sit (pure: the part and the tests share it). The
 *  whistles ARE the exhaust (M153): one at each of the ride's REAL outlets
 *  (the build script's tips), the body tucked against the measured tail, the
 *  mouth out. The M128 spots (a deck-lip rule on the cars, the bike's old
 *  side can, the monster's old header stacks) put them over the tail lamps,
 *  beside the tyre and threaded through the roll cage. */
export function whistleSpots(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[] = []): { p: THREE.Vector3; yaw: number; pitch: number }[] {
  const s = a.s;
  const ws = whistleScale(a);
  // Two whistles side by side never share space: their centres stay at least
  // a whistle's width apart (the bowl is 0.42 across), even where the real
  // tips are a close centre pair (the sports car, the bike) or one pipe (the
  // Formula) — M153: there the two merged into one brass blob.
  const HALF_GAP = 0.25 * ws;
  let spots: { p: THREE.Vector3; yaw: number; pitch: number }[];
  const ex = a.exhaust;
  if (ex && ex.dir === 'left') {
    // Side exits (the monster truck): two whistles out of the left flank,
    // spread along it and splayed a little fore and aft.
    const c = ex.pts.reduce((acc, p) => acc.add(p), V(0, 0, 0)).multiplyScalar(1 / ex.pts.length);
    spots = [-1, 1].map((sz) => ({ p: V(c.x + 0.05 * ws, c.y, c.z + sz * HALF_GAP), yaw: -Math.PI / 2 - sz * R(12), pitch: R(10) }));
  } else if (ex) {
    // (M154 polish r2: the monster truck's pair no longer moves out to the
    // tail's corners under a cape, where it sat on the mud flaps: the cape
    // now lies on its deck and ends above the lamp bar, clear of them.)
    void mods;
    const pts =
      ex.pts.length === 1
        ? [-1, 1].map((sx) => ex.pts[0]!.clone().setX(ex.pts[0]!.x + sx * HALF_GAP))
        : ex.pts.map((p) => p.clone().setX((Math.sign(p.x) || 1) * Math.max(Math.abs(p.x), HALF_GAP)));
    spots = pts.map((pt) => {
      const sx = Math.sign(pt.x) || 1;
      const face = surf?.rearZ(pt.x - 0.1 * ws, pt.x + 0.1 * ws, pt.y - 0.06 * ws, pt.y + 0.06 * ws) ?? pt.z;
      return {
        p: V(pt.x, pt.y, Math.min(face, pt.z) - 0.18 * ws),
        yaw: -sx * R(a.kind === 'bike' ? 5 : 10),
        pitch: R(a.kind === 'bike' ? 8 : 12),
      };
    });
  } else {
    const y = a.bed ? a.bumperY - 0.12 * s : a.deckY - 0.16 * s;
    spots = [-1, 1].map((sx) => ({ p: V(sx * a.halfW * 0.62, y, a.tailZ - 0.18 * s), yaw: -sx * R(12), pitch: R(14) }));
  }
  return spots;
}

/** The whistles' extents in car-local units (pure: the cape keeps its hem
 *  clear of them): each whistle's box over its body, bowl, mouthpiece, top
 *  plate and lanyard ring, after its yaw and pitch. */
export function whistleBounds(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[] = []): { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }[] {
  const ws = whistleScale(a);
  // sample points of the parts built below (whistle-local, units of ws)
  const pts: [number, number, number][] = [];
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) pts.push([sx * 0.135, sy * 0.125, 0.05 + sz * 0.28]);
  pts.push([0, 0.12, -0.16], [0, -0.26, -0.16], [0.21, -0.07, -0.16], [-0.21, -0.07, -0.16]);
  pts.push([0, 0.14, -0.6], [0, -0.08, -0.6], [0.11, 0.03, -0.6], [-0.11, 0.03, -0.6]);
  pts.push([0, 0.236, 0.33], [0.096, 0.14, 0.33], [-0.096, 0.14, 0.33], [0.085, 0.1425, 0.245], [-0.085, 0.1425, 0.095]);
  return whistleSpots(a, surf, mods).map(({ p, yaw, pitch }) => {
    const q = yawTilt(yaw, pitch);
    const b = new THREE.Box3();
    for (const [x, y, z] of pts) b.expandByPoint(V(x * ws, y * ws, z * ws).applyQuaternion(q).add(p));
    return { x0: b.min.x, x1: b.max.x, y0: b.min.y, y1: b.max.y, z0: b.min.z, z1: b.max.z };
  });
}

/** M154 r2: points over each whistle's surface (car-local; one list per
 *  whistle): the body, the bowl, the top plate, the mouthpiece and the ring,
 *  as built below, after the whistle's yaw and pitch. */
export function whistleSurfacePoints(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[] = []): THREE.Vector3[][] {
  const ws = whistleScale(a);
  const local: [number, number, number][] = [];
  const box = (w: number, h: number, d: number, cx: number, cy: number, cz: number): void => {
    const n = 12;
    for (let i = 0; i <= n; i++)
      for (let j = 0; j <= n; j++)
        for (let k = 0; k <= n; k++) {
          if (i > 0 && i < n && j > 0 && j < n && k > 0 && k < n) continue; // the surface only
          local.push([cx + (i / n - 0.5) * w, cy + (j / n - 0.5) * h, cz + (k / n - 0.5) * d]);
        }
  };
  const ellipsoid = (r: number, sy: number, cx: number, cy: number, cz: number): void => {
    for (let i = 0; i <= 10; i++)
      for (let j = 0; j < 16; j++) {
        const th = (i / 10) * Math.PI;
        const ph = (j / 16) * Math.PI * 2;
        local.push([cx + r * Math.sin(th) * Math.cos(ph), cy + r * sy * Math.cos(th), cz + r * Math.sin(th) * Math.sin(ph)]);
      }
  };
  box(0.27, 0.25, 0.56, 0, 0, 0.05);
  ellipsoid(0.21, 0.9, 0, -0.07, -0.16);
  box(0.17, 0.035, 0.15, 0, 0.125, 0.17);
  for (let i = 0; i <= 8; i++)
    for (let j = 0; j < 12; j++) {
      const z = -0.6 + (0.3 * i) / 8;
      const r = 0.085 + (0.11 - 0.085) * (i / 8);
      const ph = (j / 12) * Math.PI * 2;
      local.push([r * Math.cos(ph), 0.03 + r * Math.sin(ph), z]);
    }
  for (let j = 0; j < 16; j++) {
    const ph = (j / 16) * Math.PI * 2;
    for (const rr of [0.064, 0.096]) local.push([rr * Math.cos(ph), 0.14 + rr * Math.sin(ph), 0.33]);
  }
  return whistleSpots(a, surf, mods).map(({ p, yaw, pitch }) => {
    const q = yawTilt(yaw, pitch);
    return local.map(([x, y, z]) => V(x * ws, y * ws, z * ws).applyQuaternion(q).add(p));
  });
}

/** M154 r2: for each whistle that crosses a vertical sheet hanging at z =
 *  `hangZ` across x0..x1 (a cape's hang), the highest point of it within a
 *  hand of that plane: where the sheet's hem must clear it. */
export function whistleCrossTops(
  a: ModAnchors,
  surf: SurfaceField | null,
  hangZ: number,
  x0: number,
  x1: number,
  slab = 0.04,
  mods: readonly string[] = [],
): number[] {
  const tops: number[] = [];
  for (const pts of whistleSurfacePoints(a, surf, mods)) {
    let top = -Infinity;
    for (const p of pts) if (Math.abs(p.z - hangZ) <= slab && p.x >= x0 && p.x <= x1 && p.y > top) top = p.y;
    if (top > -Infinity) tops.push(top);
  }
  return tops;
}

export const whistle: PartFn = (a, _c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const ws = whistleScale(a);
  const spots = whistleSpots(a, opts.surface, opts.mods);
  const rings: THREE.Vector3[] = [];
  const mouths: { pos: THREE.Vector3; dir: THREE.Vector3 }[] = [];
  for (const { p, yaw, pitch } of spots) {
    const w = new THREE.Group();
    w.position.copy(p);
    w.quaternion.copy(yawTilt(yaw, pitch));
    const pb = new PartBuilder();
    pb.box(0.27 * ws, 0.25 * ws, 0.56 * ws, 0, 0, 0.05 * ws, BRASS, { radius: 0.06 * ws, bucket: 'metal' });
    pb.sphere(0.21 * ws, 0, -0.07 * ws, -0.16 * ws, BRASS, { scale: [1, 0.9, 1], bucket: 'metal' });
    pb.box(0.17 * ws, 0.035 * ws, 0.15 * ws, 0, 0.125 * ws, 0.17 * ws, DARK, { radius: 0.006 });
    pb.cyl(0.085 * ws, 0.11 * ws, 0.3 * ws, 0, 0.03 * ws, -0.45 * ws, BRASS, { rot: [-Math.PI / 2, 0, 0], bucket: 'metal' });
    pb.torus(0.08 * ws, 0.016 * ws, 0, 0.14 * ws, 0.33 * ws, BRASS, { bucket: 'metal' });
    w.add(pb.build(m));
    group.add(w);
    rings.push(V(0, 0.16 * ws, 0.33 * ws).applyQuaternion(w.quaternion).add(p));
    mouths.push({ pos: V(0, 0.03 * ws, -0.62 * ws).applyQuaternion(w.quaternion).add(p), dir: V(0, 0.25, -1).applyQuaternion(w.quaternion).normalize() });
  }
  // (M154 r2: not under a cape: below its hem the white cord read as the
  // cloth's tucked hem from the race camera on the muscle and sports cars)
  if (rings.length === 2 && rings[0] && rings[1] && !opts.mods.includes('mod.tifo')) {
    const A = rings[0];
    const B = rings[1];
    // M154: a shallow swag strung between the rings, no lower than the
    // whistles' middles — slung 0.42 ws low it hung under the bumper as a
    // white loop, nearly to the road on the low coupe (the review read it as
    // the cape's tucked hem).
    const mid = A.clone().add(B).multiplyScalar(0.5).add(V(0, -0.2 * ws, -0.12 * ws));
    const curve = new THREE.CatmullRomCurve3([A, A.clone().lerp(mid, 0.5).add(V(0, -0.06 * ws, -0.04 * ws)), mid, B.clone().lerp(mid, 0.5).add(V(0, -0.06 * ws, -0.04 * ws)), B]);
    const lan = new THREE.Mesh(new THREE.TubeGeometry(curve, 24, 0.022 * ws, 6, false), m.track(new THREE.MeshStandardMaterial({ color: WHITE, roughness: 0.8 })));
    lan.castShadow = false;
    group.add(lan);
  }
  const puffs = new SpritePool(8, 0xffffff, 0.16 * s, m, false);
  group.add(puffs.group);
  let wasBoosting = false;
  let clock = 0;
  return {
    group,
    update: (ctx, dt): void => {
      clock -= dt;
      const rising = ctx.boosting && !wasBoosting;
      wasBoosting = ctx.boosting;
      if (rising || (ctx.boosting && clock <= 0)) {
        clock = 0.5;
        for (const mo of mouths) {
          for (let i = 0; i < 2; i++) {
            puffs.emit(mo.pos, mo.dir.clone().multiplyScalar(1.6 + i * 0.6).add(V((Math.random() - 0.5) * 0.4, 0.2, 0)), 0.45, 2.2);
          }
        }
      }
      puffs.update(dt, -0.6, 1.5, 0.16 * s);
    },
  };
};

// ---------------------------------------------------------------------------
// Rally Pod — five chrome rally lamps on a bar at the front of the roof; the
// lamps click on, the beams sway with the road.
// ---------------------------------------------------------------------------
export const rally: PartFn = (a, _c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const pb = new PartBuilder();
  const lb = new PartBuilder();
  const x0 = 0;
  let z0: number;
  let n: number;
  let sp: number;
  let cr: number;
  let lift: number;
  let feet: number[];
  if (a.kind === 'bike' || a.kind === 'f1') {
    // M153: the bike's pair clamps to the top of the screen, where the chase
    // camera sees them over the rider's helmet (behind the screen they hid
    // behind it); the Formula's three ride the halo's front arch (they hung
    // in the air inside the hoop).
    const bike = a.kind === 'bike';
    const p = a.rallyAt ?? (bike ? a.local(0, 0.6, 0.95) : a.local(0, 0.28, 0.93));
    z0 = p.z;
    n = bike ? 2 : 3;
    sp = (bike ? 0.16 : 0.2) * a.k;
    cr = (bike ? 0.09 : 0.08) * s;
    lift = (bike ? 0.12 : 0.1) * s;
    feet = [-(sp * (n - 1)) / 2, (sp * (n - 1)) / 2];
  } else {
    // A little behind the windscreen header, where the roof is full width
    // (at the header the rounded front corners fell away under the outer
    // feet). M154 polish r2: a ride with its own rally mount (the open muscle
    // car: clamped to its windscreen header, clear of the driver's helmet
    // from the race camera) stands there.
    z0 = a.rallyAt ? a.rallyAt.z : a.openTop ? a.roofFront.z : a.roofFront.z + (a.roof.z - a.roofFront.z) * 0.4;
    n = 5;
    cr = 0.14 * s;
    // M153: the bar is as wide as the roof REALLY is there (halfW is the
    // body's widest point: the outer lamps hung past the coupe's, sports car's
    // and monster truck's narrow roofs on tall stilts down to the shoulders).
    const half = roofHalfAt(opts.surface, z0, 0.06, a.halfW + 0.2, a.halfW * 0.8);
    sp = Math.min(a.halfW * 0.42, (half - 0.04 * a.k) / 2);
    if (sp < 2.1 * cr) {
      n = 3;
      sp = Math.min(a.halfW * 0.42, half - 0.04 * a.k);
    }
    lift = 0.15 * s;
    feet = [-(sp * (n - 1)) / 2, 0, (sp * (n - 1)) / 2];
  }
  // The bar clears whatever the body REALLY carries under it (a light bar, a
  // curved crown the table flattened, a screen's edge, the halo).
  const zf = z0 - 0.02 * s;
  let crown = -Infinity;
  for (const fx of [...feet, 0]) {
    const top = restTopAt(a, opts.surface, x0 + fx, zf);
    if (top !== null && top > crown) crown = top;
  }
  if (crown === -Infinity) crown = a.kind === 'car' ? a.roofFront.y : (a.rallyAt?.y ?? a.roofFront.y);
  const y0 = crown + lift;
  pb.box(sp * (n - 1) + cr * 2.2, 0.1 * s, 0.11 * s, x0, y0 - 0.02 * s, zf, DARK, { radius: 0.012 });
  // Mounts: a foot under each end (and the middle on a roof), every one
  // dropped to the body the ride actually has there — M128 gave the bar two
  // fixed-height feet, which floated over a curved roof (owner).
  const barBottom = y0 - 0.07 * s;
  for (const fx of feet) {
    const roof = restTopAt(a, opts.surface, x0 + fx, zf) ?? crown - 0.05 * s;
    const top = Math.max(roof + 0.02 * s, barBottom);
    const h = Math.max(0.05 * s, top - roof + 0.02 * s);
    pb.box(0.1 * s, h, 0.14 * s, x0 + fx, top - h / 2, zf, DARK, { radius: 0.008 });
    pb.box(0.18 * s, 0.025 * s, 0.2 * s, x0 + fx, roof + 0.012 * s, zf, DARK, { radius: 0.006 });
  }
  const coneGeos: THREE.BufferGeometry[] = [];
  for (let i = 0; i < n; i++) {
    const x = x0 + (i - (n - 1) / 2) * sp;
    const y = y0 + 0.55 * cr;
    pb.cyl(cr, cr, 1.45 * cr, x, y, z0, CHROME, { rot: [Math.PI / 2, 0, 0], bucket: 'metal' });
    pb.sphere(cr * 0.98, x, y, z0 - 0.6 * cr, CHROME, { scale: [1, 1, 0.55], bucket: 'metal' });
    pb.torus(cr * 0.9, 0.015 * s, x, y, z0 + 0.72 * cr, DARK);
    lb.add(new THREE.CircleGeometry(cr * 0.86, 16), LAMP, x, y, z0 + 0.74 * cr, { bucket: 'glow' });
    coneGeos.push(lightCone(V(x, y, z0 + 0.7 * cr), V(0, -0.17, 1), 1.0 * s, 6 * s));
  }
  group.add(pb.build(m));
  const lenses = lb.build(m);
  lenses.visible = false;
  group.add(lenses);
  const cm = coneMaterial(m, 0xfff1cc);
  const merged = mergeGeometries(coneGeos, false);
  if (merged) {
    const cones = new THREE.Mesh(merged, cm);
    cones.frustumCulled = false;
    group.add(cones);
  }
  let age = 0;
  return {
    group,
    update: (ctx, dt): void => {
      age += dt;
      lenses.visible = age > 0.4;
      cm.opacity = 0.035 * clamp01((age - 0.4) / 0.7);
      group.rotation.z = damp(group.rotation.z, clamp(-ctx.lateralSpeed * 0.003, -0.05, 0.05), 6, dt);
    },
  };
};

// ---------------------------------------------------------------------------
// Trophy Topper — the World Tour Cup as a hood ornament (2.2× the concept's
// first size); the globe turns, the cup leans with the suspension.
// ---------------------------------------------------------------------------
export const trophy: PartFn = (a, _c, m, opts) => {
  const group = new THREE.Group();
  const pb = new PartBuilder();
  // The cup's foot stands on the measured hood, clear of its openings (the
  // bike's spot is its nose, right ahead of the screen glass: the table
  // height there). M154 r2: a hood with no room for the cup's clean ring
  // takes a smaller one.
  const spot = trophySpot(a, opts.surface);
  const T = trophyScale(a) * spot.k;
  const p = V(spot.x, a.hood.y, spot.z);
  if (a.kind !== 'bike') p.y = bodyY(opts, p.x, p.z, p.y);
  const prof: [number, number][] = [
    [0, 0],
    [0.15, 0],
    [0.15, 0.05],
    [0.115, 0.09],
    [0.115, 0.13],
    [0.085, 0.16],
    [0.05, 0.24],
    [0.045, 0.3],
    [0.13, 0.41],
    [0.12, 0.415],
    [0.06, 0.42],
    [0, 0.42],
  ];
  pb.add(new THREE.LatheGeometry(prof.map(([r, y]) => new THREE.Vector2(r * T, y * T)), 24), GOLD, p.x, p.y, p.z, { bucket: 'metal' });
  pb.cyl(0.137 * T, 0.137 * T, 0.04 * T, p.x, p.y + 0.07 * T, p.z, GREEN);
  pb.cyl(0.107 * T, 0.107 * T, 0.03 * T, p.x, p.y + 0.145 * T, p.z, GREEN);
  pb.torus(0.125 * T, 0.022 * T, p.x, p.y + 0.415 * T, p.z, GOLD, { rot: [Math.PI / 2, 0, 0], bucket: 'metal' });
  group.add(pb.build(m));
  const gb = new PartBuilder();
  gb.sphere(0.14 * T, 0, 0, 0, GREEN);
  gb.torus(0.14 * T, 0.008 * T, 0, 0, 0, GOLD, { rot: [Math.PI / 2, 0, 0], bucket: 'metal' });
  gb.torus(0.14 * T, 0.008 * T, 0, 0, 0, GOLD, { bucket: 'metal' });
  gb.torus(0.14 * T, 0.008 * T, 0, 0, 0, GOLD, { rot: [0, Math.PI / 2, 0], bucket: 'metal' });
  const globe = gb.build(m);
  globe.position.set(p.x, p.y + 0.53 * T, p.z);
  group.add(globe);
  return {
    group,
    update: (ctx, dt): void => {
      globe.rotation.y += dt * 0.8;
      group.rotation.x = damp(group.rotation.x, clamp(-ctx.accel * 0.004, -0.1, 0.1), 5, dt);
      group.rotation.z = damp(group.rotation.z, clamp(ctx.lateralSpeed * 0.006, -0.1, 0.1), 5, dt);
    },
  };
};

// (M154 r2: the Corner Flags live in partsFlags.ts, with the room every
// other standing mod takes.)

// ---------------------------------------------------------------------------
// Goal Wing — a GT wing on goalposts, the wing plane in the kit colour, a net
// slung forward onto the deck that billows with speed.
// ---------------------------------------------------------------------------
/** The Goal Wing's frame (pure: the part and the cape share it): the goal's
 *  span between the posts, the posts' height, the feet's height and the z
 *  the goal stands at. */
export function goalwingFrame(a: ModAnchors, surf: SurfaceField | null): { span: number; h: number; y: number; z: number } {
  const s = a.s;
  if (a.wing) {
    // M153: a ride with a stock rear wing (the coupe, the sports car) gets
    // its goal BUILT ON that wing — the posts stand on its endplates, so the
    // stock blade reads as the goal line under the crossbar (M128 stood a
    // second wing over and ahead of it: two stacked wings, the far post
    // buried in the stock wing's tip).
    return { span: a.wing.half * 2, h: 0.45 * s, y: a.wing.y + 0.03 * a.k, z: a.wing.z };
  }
  const span = a.halfW * 1.8;
  // The open muscle car carries it toward the tail lip (right behind the
  // seats it read as a roll hoop); the posts' feet stand on the measured
  // deck.
  const z = a.openTop ? a.tailZ + 0.42 * s : a.deckZ;
  const foot = (x: number): number => surf?.topIn(x - 0.09 * s, x + 0.09 * s, z - 0.09 * s, z + 0.09 * s) ?? a.deckY;
  return { span, h: 0.62 * s, y: Math.max(foot(-span / 2), foot(span / 2)), z };
}

/** M154: where the Goal Wing stands between the race camera and whatever
 *  lies under it (the cape's cloth): boxes in car-local (z, y) spanning the
 *  width — the crossbar with its wing plane, and the net slung forward from
 *  the crossbar onto the body. */
export function goalwingCovers(a: ModAnchors, surf: SurfaceField | null, net = true): { z0: number; z1: number; y0: number; y1: number }[] {
  if (a.kind !== 'car') return [];
  const s = a.s;
  const { h, y, z } = goalwingFrame(a, surf);
  const top = y + h;
  const netZ = z + 0.58 * s;
  const netY = Math.min(top - 0.12 * s, (surf?.topAt(0, netZ) ?? y) + 0.03 * s);
  const bar = { z0: z - 0.2 * s, z1: z + 0.2 * s, y0: top - 0.04 * s, y1: top + 0.11 * s };
  // (M154 polish r2: `net` false = the net's cords are seen through: only
  // the crossbar and its wing plane hide what lies under them)
  return net ? [bar, { z0: z, z1: netZ, y0: netY, y1: top }] : [bar];
}

// (M154 r2: the Goal Wing's builder lives in partsSoft beside the cape, whose
// rail it stands on when both are worn.)

// ---------------------------------------------------------------------------
// Ball Mud Flaps — rubber flaps behind the rear tyres with a soccer-ball
// roundel; they swing back with speed and flutter on bumps.
// ---------------------------------------------------------------------------
/** Where the mud flaps hang (pure: the part and the cape share it), on the
 *  +X side (mirrored): the flap's centre x and width, its top and height, the
 *  z it hangs at, and where a bracket meets the body (null: none). */
export function mudflapLayout(
  a: ModAnchors,
  surf: SurfaceField | null,
): { x: number; w: number; top: number; h: number; zb: number; mount: number | null } {
  const s = a.s;
  // Where the flaps hang (M153, measured per ride):
  //  - behind the rear tyres where that slot is open (the pickup);
  //  - the monster truck: off a bracket from the frame, behind tyres that
  //    stand clear of its shell (M128's flaps hung in mid-air there);
  //  - the coupe, sports car and muscle car wrap their haunches round the
  //    tyre down to the rocker, so a flap behind the tyre sat INSIDE the
  //    bodywork (two chrome rod ends showed): theirs hang behind the tail's
  //    corners, clear of the exhaust tips, where the chase camera sees them.
  let x = a.wheelX;
  let w = Math.min(a.wheelW * 1.15, 0.44 * s);
  let zb = a.wheelZ - a.wheelR - 0.05 * s;
  let top = Math.min(a.wheelY + a.wheelR * 0.55, a.wheelY + 0.25 * s);
  let h = Math.min(top - 0.05, 0.6 * s);
  /** Where a bracket meets the body (null: the rod alone carries the flap). */
  let mount: number | null = null;
  if (a.isMonster) {
    w = Math.min(a.wheelW * 0.8, 0.6 * s);
    top = a.sillY - 0.02 * a.k;
    h = top - (a.wheelY - a.wheelR * 0.45);
    zb = a.wheelZ - a.wheelR - 0.06 * s;
    mount = surf?.rearZ(x - w / 2, x + w / 2, top - 0.15, top + 0.05) ?? a.tailZ;
  } else if (surf) {
    let covered = 0;
    let total = 0;
    for (let y = top - h + 0.02; y <= top; y += 0.04) {
      total++;
      const side = surf.sideX(zb - 0.04, zb + 0.04, y - 0.02, y + 0.02);
      if (side !== null && side > x - 0.02) covered++;
    }
    if (total > 0 && covered / total > 0.5) {
      w *= 0.8;
      top = a.wheelY + 0.12 * s;
      h = top - 0.05;
      // Clear of the exhaust tips beside them.
      const tips = a.exhaust?.dir === 'back' ? Math.max(...a.exhaust.pts.map((p) => Math.abs(p.x))) : 0;
      x = Math.max(x, tips + 0.07 * a.k + w / 2);
      const face = surf.rearZ(x - w / 2, x + w / 2, 0.05, top) ?? a.tailZ;
      mount = face;
      zb = face - 0.04 * s;
    }
  }
  return { x, w, top, h, zb, mount };
}

export const mudflaps: PartFn = (a, _c, m, opts) => {
  const group = new THREE.Group();
  if (a.kind !== 'car') return { group };
  const s = a.s;
  const surf = opts.surface;
  const pb = new PartBuilder();
  const pivots: THREE.Group[] = [];
  const badgeMat = m.track(new THREE.MeshBasicMaterial({ map: roundelTexture(), transparent: true, depthWrite: false }));
  const { x, w, top, h, zb, mount } = mudflapLayout(a, surf);
  // The ball roundel goes on the part of the flap the chase camera SEES: below
  // any bodywork that reaches back past the flap (the pickup's step-bumper
  // ends hid half the ball), as big as the open patch allows, nearest the
  // flap's middle. Worked out on the +X flap, mirrored on the other.
  let badgeX = 0;
  let badgeY = -h * 0.55;
  let badge = Math.min(0.28 * s, w * 0.85);
  if (surf) {
    const nx = Math.max(2, Math.round(w / 0.03));
    const ny = Math.max(2, Math.round(h / 0.03));
    const cw = w / nx;
    const ch = h / ny;
    const hidden = new Uint8Array(nx * ny);
    for (let i = 0; i < nx; i++) {
      const x0 = x - w / 2 + i * cw;
      for (let j = 0; j < ny; j++) {
        const y = top - (j + 0.5) * ch;
        const back = surf.rearZ(x0, x0 + cw, y - ch / 2, y + ch / 2);
        if (back === null || back >= zb - 0.02) continue;
        // The camera looks down from behind: a part reaching `d` past the
        // flap also hides about d * tan(elevation) of it below its edge.
        const drop = (zb - back) * 0.45;
        for (let jj = j; jj < ny && top - (jj + 0.5) * ch >= y - drop; jj++) hidden[jj * nx + i] = 1;
      }
    }
    if (hidden.some((v) => v === 1)) {
      const free = (i0: number, j0: number, n: number): boolean => {
        for (let j = j0; j < j0 + n; j++) for (let i = i0; i < i0 + n; i++) if (hidden[j * nx + i]) return false;
        return true;
      };
      const want = Math.min(nx, ny, Math.ceil(badge / Math.min(cw, ch)));
      let found = false;
      for (let n = want; n >= 3 && !found; n--) {
        let best = Infinity;
        for (let j = 0; j + n <= ny; j++) {
          for (let i = 0; i + n <= nx; i++) {
            if (!free(i, j, n)) continue;
            const cx = (i + n / 2) * cw - w / 2;
            const cy = -(j + n / 2) * ch;
            const d = Math.hypot(cx, cy + h * 0.55);
            if (d < best) {
              best = d;
              badgeX = cx;
              badgeY = cy;
              badge = Math.min(badge, n * Math.min(cw, ch) * 0.95);
              found = true;
            }
          }
        }
      }
    }
  }
  for (const sx of [-1, 1]) {
    const xs = sx * x;
    // The hanger: a rod across the flap's top (no wider than the flap — M128's
    // ran past the truck's sides), and a bracket back to the body where the
    // flap hangs clear of it.
    pb.rod(V(xs - w / 2, top, zb + 0.03 * s), V(xs + w / 2, top, zb + 0.03 * s), 0.022 * s, CHROME, 'metal');
    if (mount !== null && mount - zb > 0.05 * s) {
      for (const ox of [-0.3, 0.3]) pb.rod(V(xs + ox * w, top, mount + 0.04 * s), V(xs + ox * w, top, zb + 0.03 * s), 0.02 * s, DARK);
    }
    const piv = new THREE.Group();
    piv.position.set(xs, top, zb);
    const fb = new PartBuilder();
    fb.box(w, h, 0.035 * s, 0, -h / 2, 0, RUBBER, { radius: 0.006 });
    piv.add(fb.build(m));
    const rd = new THREE.Mesh(new THREE.PlaneGeometry(badge, badge), badgeMat);
    rd.position.set(sx * badgeX, badgeY, -0.022 * s);
    rd.rotation.y = Math.PI;
    piv.add(rd);
    pivots.push(piv);
    group.add(piv);
  }
  group.add(pb.build(m));
  return {
    group,
    update: (ctx, dt): void => {
      const wind = windOf(ctx);
      const target = 0.38 * wind + 0.07 * Math.sin(ctx.t * 12) * wind;
      for (const p of pivots) p.rotation.x = damp(p.rotation.x, target, 6, dt);
    },
  };
};

export type { ModPart };
export { alignY };
